import { z } from 'zod'

// Common validation patterns
export const emailSchema = z.string().email('Invalid email format')
export const passwordSchema = z.string().min(6, 'Password must be at least 6 characters')
export const requiredStringSchema = z.string().min(1, 'This field is required')
export const positiveNumberSchema = z.number().min(0, 'Must be a positive number')
export const priceSchema = z.number().min(0, 'Price must be 0 or greater')

// User/Staff related schemas
export const userRoles = [
  'admin',
  'manager',
  'inventory_manager',
  'counter',
  'accountant',
] as const
export const userRoleSchema = z.enum(userRoles)

const profileImageUrlSchema = z
  .string()
  .max(600_000, 'Image data is too large — use a smaller file or an HTTPS link')
  .optional()
  .refine(
    (val) => {
      if (val == null || val.trim() === '') return true
      const t = val.trim()
      return (
        t.startsWith('https://') ||
        t.startsWith('http://') ||
        t.startsWith('data:image/png') ||
        t.startsWith('data:image/jpeg') ||
        t.startsWith('data:image/jpg') ||
        t.startsWith('data:image/webp') ||
        t.startsWith('data:image/gif')
      )
    },
    { message: 'Use an http(s) image URL or upload a PNG, JPEG, WebP, or GIF' }
  )

/** Menu product photo: HTTPS/HTTP link or browser data URL from file upload (~5MB file → ~7M chars base64) */
const productImageUrlSchema = z
  .string()
  .max(8_000_000, 'Image data is too large — use a file under 5MB or an HTTPS link')
  .refine(
    (val) => {
      if (val == null || val.trim() === '') return true
      const t = val.trim()
      return (
        t.startsWith('https://') ||
        t.startsWith('http://') ||
        t.startsWith('data:image/png') ||
        t.startsWith('data:image/jpeg') ||
        t.startsWith('data:image/jpg') ||
        t.startsWith('data:image/webp') ||
        t.startsWith('data:image/gif')
      )
    },
    { message: 'Use an http(s) image URL or upload a PNG, JPEG, WebP, or GIF' }
  )

/**
 * Opt-in to the day-close Z report email (F-RPT-N16). Defaults to false on
 * create so a new staff account never starts out receiving the restaurant's
 * sales figures without someone deciding it should.
 */
export const emailReportsEnabledSchema = z.boolean().optional()

export const createUserSchema = z.object({
  username: requiredStringSchema.min(3, 'Username must be at least 3 characters'),
  email: emailSchema,
  password: passwordSchema,
  first_name: requiredStringSchema,
  last_name: requiredStringSchema,
  role: userRoleSchema,
  profile_image_url: profileImageUrlSchema,
  email_reports_enabled: emailReportsEnabledSchema,
})

export const updateUserSchema = z.object({
  id: z.string().or(z.number()),
  username: requiredStringSchema.min(3, 'Username must be at least 3 characters').optional(),
  email: emailSchema.optional(),
  password: passwordSchema.optional(),
  first_name: requiredStringSchema.optional(),
  last_name: requiredStringSchema.optional(),
  role: userRoleSchema.optional(),
  profile_image_url: profileImageUrlSchema,
  email_reports_enabled: emailReportsEnabledSchema,
})

// Product related schemas
export const productStatusValues = ['active', 'inactive'] as const
export const productStatusSchema = z.enum(productStatusValues)

export const projectedCostBasisValues = ['amount', 'percent'] as const
export const projectedCostBasisSchema = z.enum(projectedCostBasisValues)

/**
 * Optional projected cost block. Used by the menu form and the bulk costing
 * tab. Cross-field rules:
 *  - basis='amount': `projected_cost` is the canonical figure (>= 0). The
 *    percent field is ignored / cleared.
 *  - basis='percent': `projected_cost_percent` (0-100) is required; the
 *    server (re)computes `projected_cost` from `price * percent / 100`.
 *  - If neither cost nor percent is provided, the block is "empty" and the
 *    item simply has no projected cost set — the row is excluded from
 *    margin math (and flagged as "no cost set" in the items report).
 */
export const createProductSchema = z.object({
  name: requiredStringSchema.min(2, 'Product name must be at least 2 characters'),
  description: z.string().optional(),
  price: priceSchema,
  // DB uses UUID strings; Number(uuid) is NaN and becomes JSON null — breaks create/update.
  category_id: z
    .union([z.string(), z.number()])
    .transform((val) => (typeof val === 'string' ? val.trim() : String(val)))
    .pipe(z.string().min(1, 'Category is required')),
  image_url: productImageUrlSchema,
  status: productStatusSchema.default('active'),
  // No default. A zod default here was the whole bug: it stamped '98017000'
  // — the "messes and hostels" services code — onto every product created
  // through the admin UI, which pre-empted the backend's category-default
  // lookup (routes.go createProduct) so that fallback never once fired.
  // Omitting the field lets the server derive it from the category.
  pct_code: z.string().optional(),
  /** Optional FBR DI line overrides; empty values use SN002-aligned defaults when building invoices. */
  fbr_uom: z.string().optional().default(''),
  fbr_sale_type: z.string().optional().default(''),
  fbr_sro_schedule_no: z.string().optional().default(''),
  fbr_sro_item_serial_no: z.string().optional().default(''),
  fbr_fed_payable: z.number().optional().default(0),
  // Projected costing — see comment block above for the cross-field rules.
  projected_cost_basis: projectedCostBasisSchema.optional(),
  projected_cost: z.number().min(0, 'Cost must be 0 or greater').nullable().optional(),
  projected_cost_percent: z
    .number()
    .min(0, 'Percent must be between 0 and 100')
    .max(100, 'Percent must be between 0 and 100')
    .nullable()
    .optional(),
  // Inventory (F-RETAIL-12). Every 'stock' product is tracked — there is no
  // opt-out — so the only choice here is stock vs service. The quantity-side
  // fields seed the product's shadow stock item on create; afterwards the
  // Inventory screens own them (on-hand is never edited from this form).
  kind: z.enum(['stock', 'service']).default('stock'),
  unit: z.string().trim().min(1).max(30).default('each'),
  sku: z.string().trim().max(50).optional(),
  barcode: z.string().trim().max(50).optional(),
  unit_cost: z.number().min(0, 'Cost must be 0 or greater').nullable().optional(),
  reorder_level: z.number().min(0, 'Reorder level must be 0 or greater').optional(),
  opening_stock: z.number().min(0, 'Opening stock must be 0 or greater').optional(),
  supplier_id: z.string().uuid().nullable().optional(),
  // Overlapping reporting labels (product_tags). The full set replaces the
  // product's tags on save. Managed by the form's Tags section and attached at
  // submit (not a registered RHF field), mirroring `components` above.
  tag_ids: z.array(z.string().uuid()).optional(),
})

// `.partial()` / `.extend()` keep working since createProductSchema isn't
// refined — partial updates send any subset of fields. The "I'm filling in
// the costing block now" guard is enforced by the form UI (disabling the
// save button until a value is entered) rather than a Zod refinement, so
// users can still submit with the section collapsed (= leave costing
// untouched) and a separate "Remove" button can submit explicit nulls.
export const updateProductSchema = createProductSchema.partial().extend({
  id: z.string().or(z.number()),
})

// Category related schemas
export const createCategorySchema = z.object({
  name: requiredStringSchema.min(2, 'Category name must be at least 2 characters'),
  description: z.string().optional(),
  image_url: z.string().url().optional().or(z.literal('')),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color').default('#6B7280'),
  sort_order: z.number().min(0).default(0),
  /** 'none' = unassigned; otherwise kitchen_stations.id */
  kitchen_station_id: z.string().optional().default('none'),
  /** Default PCT/HS for new products (FBR digital invoice lines). */
  default_pct_code: z.string().max(32).optional(),
})

export const updateCategorySchema = createCategorySchema.partial().extend({
  id: z.string().or(z.number()),
})

// Table related schemas
export const tableStatusValues = ['available', 'occupied', 'reserved', 'maintenance'] as const
export const tableStatusSchema = z.enum(tableStatusValues)

export const createTableSchema = z.object({
  table_number: requiredStringSchema.min(1, 'Table number is required'),
  seats: z.number().min(1, 'Table must have at least 1 seat').max(20, 'Maximum 20 seats per table'),
  status: tableStatusSchema.default('available'),
  location: z.string().optional(),
})

export const updateTableSchema = createTableSchema.partial().extend({
  id: z.string().or(z.number()),
})

// Order related schemas
export const orderTypeValues = ['dine-in', 'take-away', 'delivery'] as const
export const orderTypeSchema = z.enum(orderTypeValues)

export const orderStatusValues = ['pending', 'confirmed', 'preparing', 'ready', 'served', 'cancelled'] as const
export const orderStatusSchema = z.enum(orderStatusValues)

export const orderItemSchema = z.object({
  product_id: z.number(),
  quantity: z.number().min(1, 'Quantity must be at least 1'),
  notes: z.string().optional(),
})

export const createOrderSchema = z.object({
  table_id: z.number().optional(),
  customer_name: z.string().optional(),
  order_type: orderTypeSchema,
  notes: z.string().optional(),
  items: z.array(orderItemSchema).min(1, 'Order must have at least one item'),
})

// Settings schemas
export const posSettingsSchema = z.object({
  restaurant_name: requiredStringSchema,
  address: z.string().optional(),
  phone: z.string().optional(),
  email: emailSchema.optional(),
  tax_rate: z.number().min(0).max(1), // 0.08 for 8%
  currency_symbol: requiredStringSchema.default('Rs.'),
  receipt_footer: z.string().optional(),
  auto_print_receipts: z.boolean().default(false),
  order_timeout_minutes: z.number().min(1).max(120).default(30),
})

// Login schema
export const loginSchema = z.object({
  username: requiredStringSchema,
  password: requiredStringSchema,
})

/** Marketing QR (Admin Settings) — must match backend validateMarketingQrCodes */
const marketingQrTargetUrlSchema = z
  .string()
  .max(2048)
  .refine(
    (s) => {
      const t = s.trim()
      if (t === '') return true
      try {
        const u = new URL(t)
        return u.protocol === 'http:' || u.protocol === 'https:'
      } catch {
        return false
      }
    },
    { message: 'Use a valid http(s) URL or leave empty for an unused slot' }
  )

/** Values accepted by `qr-code-styling` (must stay aligned with backend). */
export const marketingQrDotsTypes = [
  'square',
  'dots',
  'rounded',
  'extra-rounded',
  'classy',
  'classy-rounded',
] as const
export const marketingQrCornerSquareTypes = [
  'square',
  'dot',
  'rounded',
  'extra-rounded',
  'dots',
  'classy',
  'classy-rounded',
] as const
export const marketingQrCornerDotTypes = [
  'square',
  'dot',
  'rounded',
  'extra-rounded',
  'dots',
  'classy',
  'classy-rounded',
] as const
export const marketingQrErrorCorrectionLevels = ['L', 'M', 'Q', 'H'] as const

export const marketingQrStyleSchema = z.object({
  dark_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Module color must be #RRGGBB'),
  light_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Background must be #RRGGBB'),
  margin_modules: z.number().int().min(0).max(8).default(3),
  dots_type: z.enum(marketingQrDotsTypes).default('square'),
  corner_square_type: z.enum(marketingQrCornerSquareTypes).default('square'),
  corner_dot_type: z.enum(marketingQrCornerDotTypes).default('square'),
  /**
   * Derived, not chosen: the backend's normalizeFixedStyle pins this to H when
   * logo_enabled and Q otherwise, because a centre logo is only survivable at
   * H. Still validated here for API / legacy payloads.
   */
  error_correction: z.enum(marketingQrErrorCorrectionLevels).default('Q'),
  /**
   * Draw the tenant's receipt logo in the centre of the code. Defaults to true
   * to match the backend, where an absent key on a pre-existing row means
   * "branded" rather than "off" — see marketingQRStyle.LogoEnabled.
   */
  logo_enabled: z.boolean().default(true),
  /**
   * Fraction of the error-correction budget spent on the logo — NOT a fraction
   * of the code's area. Mirrors QR_LOGO_SCALE_* in lib/brandedQr.ts and
   * marketingQRLogoScale* in backend/internal/handlers/marketing_qr.go.
   */
  logo_scale: z.number().min(0).max(0.6).default(0.45),
})

export const marketingQrItemSchema = z.object({
  id: z.string().uuid(),
  label: z.string().max(100),
  target_url: marketingQrTargetUrlSchema,
  style: marketingQrStyleSchema,
})

export const marketingQrCodesPayloadSchema = z.object({
  items: z.array(marketingQrItemSchema).max(4),
})

/** Exactly four slots — persisted shape (backend allows up to 4; we always save 4). */
export const marketingQrCodesPersistSchema = z.object({
  items: z.array(marketingQrItemSchema).length(4),
})

// Export types
export type MarketingQrCodeItem = z.infer<typeof marketingQrItemSchema>
export type MarketingQrCodesPayload = z.infer<typeof marketingQrCodesPayloadSchema>
export type CreateUserData = z.infer<typeof createUserSchema>
export type UpdateUserData = z.infer<typeof updateUserSchema>
export type CreateProductData = z.infer<typeof createProductSchema>
export type UpdateProductData = z.infer<typeof updateProductSchema>
export type CreateCategoryData = z.infer<typeof createCategorySchema>
export type UpdateCategoryData = z.infer<typeof updateCategorySchema>
export type CreateTableData = z.infer<typeof createTableSchema>
export type UpdateTableData = z.infer<typeof updateTableSchema>
export type CreateOrderData = z.infer<typeof createOrderSchema>
export type LoginData = z.infer<typeof loginSchema>
export type POSSettingsData = z.infer<typeof posSettingsSchema>
